/** This file holds static information not provided by other means */

// Logo assets follow the convention in src/assets/images/integrations/README.md:
// `<system-key>-logo.*` (wide lockup) + `<system-key>-mark.*` (square tile mark).
import deephubLogo from 'src/assets/images/integrations/deephub-logo.png';
import deephubMark from 'src/assets/images/integrations/deephub-mark.png';
import solumAimsLogo from 'src/assets/images/integrations/solum-aims-logo.png';
import omloxHubLogo from 'src/assets/images/integrations/omlox-hub-logo.svg';
import omloxHubMark from 'src/assets/images/integrations/omlox-hub-mark.svg';
import sepiooLogo from 'src/assets/images/integrations/sepioo-logo.svg';
import sepiooMark from 'src/assets/images/integrations/sepioo-mark.svg';
import solumAimsSaasLogo from 'src/assets/images/integrations/solum-aims-saas-logo.png';
import steuteSensorbridgeMark from 'src/assets/images/integrations/steute-sensorbridge-mark.png';
import zkongLogo from 'src/assets/images/integrations/zkong-logo.png';
import zkongMark from 'src/assets/images/integrations/zkong-mark.png';
import { DeviceIntegrationDescription } from '@ekkogmbh/apisdk';

/* Selectable service integrations */
export const SERVICE_WHITELIST = [
  'Solum',
  'Solum-Cloud',
  'PdiDigital',
  'Zkong',
  'Steute',
  'DeepHub',
  'Omlox',
  'Simulated',
];

export type Manufacturer = {
  name: string;
  icon?: string;
};
const MANUFACTURER_MAP: Record<string, Manufacturer> = {
  solum: { name: 'SoluM' },
  pdi: { name: 'PDI Digitial', icon: sepiooMark },
  steute: { name: 'Steute', icon: steuteSensorbridgeMark },
  flowcate: { name: 'Flowcate', icon: deephubMark },
  omlox: { name: 'omlox', icon: omloxHubMark },
  ekko: { name: 'Ekko' },
  zkong: { name: 'Zkong', icon: zkongMark },
};

export type ServiceKnowledge = {
  displayName?: string;
  manufacturerId: keyof typeof MANUFACTURER_MAP;
  description?: string;
  /** Wide logo lockup — roomy surfaces only (details-drawer header), never the square tile. */
  logo?: string;
  /** Square icon mark (`*-mark` asset) */
  icon?: string;
};
const SERVICE_KNOWLEDGE_MAP: Record<string, ServiceKnowledge> = {
  Solum: {
    displayName: 'AIMS',
    description: 'SoluM ESL platform (on premise).',
    logo: solumAimsLogo,
    icon: solumAimsLogo,
    manufacturerId: 'solum',
  },
  'Solum-Cloud': {
    displayName: 'AIMS SaaS',
    description: 'SoluM ESL platform (cloud).',
    logo: solumAimsSaasLogo,
    icon: solumAimsSaasLogo,
    manufacturerId: 'solum',
  },
  PdiDigital: {
    displayName: 'Sepioo',
    description: 'E-paper shelf labels and displays by PDI Digital.',
    manufacturerId: 'pdi',
    logo: sepiooLogo,
    icon: sepiooMark,
  },
  Steute: {
    displayName: 'Sensor Bridge',
    description: 'Steute wireless switches and sensors via SensorBridge.',
    manufacturerId: 'steute',
  },
  Simulated: {
    description: 'Fully integrated service for simulated, interactive labels.',
    manufacturerId: 'ekko',
  },
  DeepHub: {
    displayName: 'DeepHub',
    description: 'Flowcate DeepHub RTLS — omlox hub for real-time location tracking.',
    manufacturerId: 'flowcate',
    logo: deephubLogo,
    icon: deephubMark,
  },
  Omlox: {
    displayName: 'omlox Hub',
    description: 'Generic connector for any omlox-compliant hub.',
    manufacturerId: 'omlox',
    logo: omloxHubLogo,
    icon: omloxHubMark,
  },
  Zkong: {
    description: 'Zkong Service (beta)',
    manufacturerId: 'zkong',
    logo: zkongLogo,
    icon: zkongMark,
  },
};

// Accessors

export function getManufacturer(id: string): Manufacturer | undefined {
  return MANUFACTURER_MAP[id] ?? undefined;
}

export function getServiceKnowledge(serviceName: string): ServiceKnowledge | undefined {
  if (serviceName.startsWith('Omlox')) {
    return SERVICE_KNOWLEDGE_MAP['Omlox'];
  }

  return SERVICE_KNOWLEDGE_MAP[serviceName] ?? undefined;
}

export function getDeviceManufacturer(
  technology: string,
  integration?: DeviceIntegrationDescription,
): Manufacturer | undefined {
  if (integration !== undefined) {
    for (const s of integration.services) {
      const serviceInfo = getServiceKnowledge(s);
      if (serviceInfo !== undefined) {
        const manufacturer = getManufacturer(serviceInfo.manufacturerId);
        if (manufacturer !== undefined) {
          return manufacturer;
        }
      }
    }
  }

  // try old names
  switch (technology) {
    case 'solum':
    case 'solumcloud':
      return getManufacturer('solum');
    case 'pdi-digital':
      return getManufacturer('pdi');
    default:
      if (technology.startsWith('omlox')) {
        return getManufacturer('omlox');
      }

      return getManufacturer(technology);
  }
}
